import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Input, ButtonVariant, Notify, Button, ButtonType } from 'components/core';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  rolesState,
  currentRoleState,
  Role,
  newItemState,
  usersForCurrentRoleState,
  editItemState,
} from 'states';
import { useRolesService, useUserService } from 'services';
import { useEffect } from 'react';

interface IFormValues {
  name: string;
}

const handleNameChange = (setValue: any) => (e: any) => {
  setValue('name', e.target.value, { shouldValidate: true });
};

const handleCancel =
  (roles: any[], setRoles: any, setIsNewItem: any, setCurrentRole: any, setEditCurrentRole: any) =>
  (e: any) => {
    e.preventDefault();
    const unsavedRoleIndex = roles.findIndex((role: Role) => role.id === undefined);
    if (unsavedRoleIndex !== -1) {
      const updatedRoles = [...roles];
      updatedRoles.splice(unsavedRoleIndex, 1);
      setRoles(updatedRoles);
      setIsNewItem(false);
      setCurrentRole({});
    }
    setEditCurrentRole(false);
  };

export const NewRole = () => {
  const { t } = useTranslation();
  const [currentRoleDetail, setCurrentRole] = useRecoilState(currentRoleState);
  const [roles, setRoles] = useRecoilState(rolesState);
  const setIsNewItem = useSetRecoilState(newItemState);
  const [editCurrentRole, setEditCurrentRole] = useRecoilState(editItemState);
  const setUsersForCurrentRole = useSetRecoilState(usersForCurrentRoleState);
  const roleService = useRolesService();
  const userService = useUserService();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('ROLE.NAME_REQUIRED')),
  });

  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: currentRoleDetail.name || '',
    },
  };

  const { register, handleSubmit, formState, setValue } = useForm<IFormValues>(formOptions);
  const { errors, isSubmitting } = formState;

  useEffect(() => {
    setValue('name', currentRoleDetail.name || '');
  }, [editCurrentRole, currentRoleDetail]);

  const onSubmit = (values: IFormValues) => {
    const roleDetail = { ...currentRoleDetail, name: values.name };
    roleService.createOrUpdateRole(roleDetail).then(
      (response) => {
        if (roleDetail.id === undefined) {
          Notify.success(t('ROLE.SAVE_SUCCESS'));
          setIsNewItem(false);
          const savedRoleId = response[0].id;
          if (savedRoleId !== undefined) {
            userService.getUsersForRole(savedRoleId).then((usersForSelectedRole) => {
              setUsersForCurrentRole(usersForSelectedRole);
            });
          }
        } else {
          Notify.success(t('ROLE.UPDATE_SUCCESS'));
          setEditCurrentRole(false);
        }
        setCurrentRole(response[0]);
        roleService.getRoles().then((rolesResponse) => {
          setRoles(rolesResponse);
        });
      },
      (error) => {
        Notify.error(error);
      }
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="col-sm-12 col-md-12 col-lg-6 mt-4 mb-3">
          <div className="mb-4">
            <Input
              type="text"
              showDescription={false}
              showTitle={true}
              title={t('ROLE.FIELD_NAME')}
              showOptTxt={false}
              placeholder={t('ROLE.PLACEHOLDER_NAME')}
              {...register('name')}
              className={`${errors.name ? 'is-invalid' : ''}`}
              onChange={handleNameChange(setValue)}
            />
            <div className="invalid-feedback">{errors.name?.message}</div>
          </div>
        </div>
        <div className="col-12 p-0 m-0">
          <div className="clearfix">
            <div className="float-start">
              <Button
                className="w-100"
                type={ButtonType.secondary}
                variant={ButtonVariant.outline}
                onClick={handleCancel(
                  roles,
                  setRoles,
                  setIsNewItem,
                  setCurrentRole,
                  setEditCurrentRole
                )}
              >
                <span className={`material-icons me-1 valign_sub`}>close</span>
                {t('ACTION.CANCEL')}
              </Button>
            </div>
            <div className="float-end end-0">
              <Button className="w-100" type={ButtonType.secondary} disabled={isSubmitting}>
                {isSubmitting && <span className="spinner-border spinner-border-sm me-3"></span>}
                <span className={`material-icons me-1 valign_sub`}>done</span>
                {t('ACTION.SAVE')}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};
